import {
	AnnouncementFormValues,
	BlockFileEntry
} from "../adapters/formValuesToAnnouncement";
import { ContentBlock } from "../components/ContentBlocksEditor/ContentBlocksEditor";

const escapeAttr = (s: string) =>
	s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

/**
 * Render a ContentBlock[] to body HTML. Kept as a fallback for the
 * read-only "sent" view and legacy consumers — `contentBlocks` is the
 * authoritative structured representation. Image/video blocks whose
 * file is still mid-upload (no persisted `content` yet) contribute
 * nothing here; the next sync pass, fired after the upload resolves,
 * will include them.
 */
export function serialiseBlocksToBodyHtml(blocks: ContentBlock[]): string {
	return blocks
		.map((b) => {
			if (b.type === "text") return b.content || "";
			if (b.type === "image" && b.content) {
				const altAttr = b.alt ? ` alt="${escapeAttr(b.alt)}"` : ' alt=""';
				return `<img src="${escapeAttr(b.content)}"${altAttr}>`;
			}
			if (b.type === "video" && b.content) {
				const labelAttr = b.alt
					? ` aria-label="${escapeAttr(b.alt)}"`
					: ' aria-label="Announcement video"';
				return `<video controls${labelAttr}><source src="${escapeAttr(b.content)}"></video>`;
			}
			return "";
		})
		.filter(Boolean)
		.join("");
}

/**
 * Extract any mid-upload block files from the blocks array. Kept for
 * legacy consumers; the editor now uploads on file-select so by the
 * time the user hits Save this list is empty.
 */
export function extractBlockFiles(blocks: ContentBlock[]): BlockFileEntry[] {
	const result: BlockFileEntry[] = [];
	blocks.forEach((b, i) => {
		if (b.file && (b.type === "image" || b.type === "video")) {
			result.push({ index: i, type: b.type, file: b.file });
		}
	});
	return result;
}

/**
 * Serialise blocks for persistence — structured form that round-trips
 * back into ContentBlock[] on reopen without the lossiness of body HTML
 * parsing (which merges adjacent paragraphs into a single block, etc).
 *
 * Image / video blocks are only included when they have a persisted
 * reference in `content`. A media block mid-upload (file picked, no
 * reference yet) OR never-filled (no file, no reference) contributes
 * nothing — persisting an empty reference would land a broken block
 * on reopen. This is defence-in-depth alongside the form-level
 * `hasUploadingBlock` guard that keeps Save/Send disabled until
 * everything has uploaded.
 */
export function serialiseBlocksForPersistence(
	blocks: ContentBlock[]
): MeeAnnouncementContentBlock[] {
	const out: MeeAnnouncementContentBlock[] = [];
	for (const b of blocks) {
		if (b.type === "text") {
			out.push({ type: b.type, content: b.content || "" });
			continue;
		}
		if ((b.type === "image" || b.type === "video") && b.content) {
			out.push({ type: b.type, content: b.content });
		}
	}
	return out;
}

let blockParseCounter = 0;
const generateParseId = () => `parsed-${++blockParseCounter}-${Date.now()}`;

function parseBody(doc: Document): ContentBlock[] {
	const nodes = Array.from(doc.body.childNodes);
	const blocks: ContentBlock[] = [];
	let textSegment = "";

	const flushText = () => {
		if (textSegment.trim()) {
			blocks.push({
				id: generateParseId(),
				type: "text",
				content: textSegment,
				file: null,
				previewUrl: null
			});
		}
		textSegment = "";
	};

	for (const node of nodes) {
		if (node instanceof HTMLImageElement) {
			flushText();
			blocks.push({
				id: generateParseId(),
				type: "image",
				content: node.getAttribute("src") || "",
				file: null,
				previewUrl: null
			});
		} else if (node instanceof HTMLVideoElement) {
			flushText();
			const source = node.querySelector("source");
			const src = source?.getAttribute("src") || node.getAttribute("src") || "";
			blocks.push({
				id: generateParseId(),
				type: "video",
				content: src,
				file: null,
				previewUrl: null
			});
		} else if (node instanceof HTMLElement) {
			textSegment += node.outerHTML;
		} else if (node.textContent?.trim()) {
			textSegment += node.textContent;
		}
	}

	flushText();
	return blocks;
}

export function contentBlocksToBlocks(
	contentBlocks: MeeAnnouncementContentBlock[]
): ContentBlock[] {
	return contentBlocks.map((cb) => ({
		id: generateParseId(),
		type: cb.type,
		content: cb.content,
		file: null,
		previewUrl: null
	}));
}

export function parseBodyToBlocks(
	body: string | null | undefined,
	contentBlocks?: MeeAnnouncementContentBlock[]
): ContentBlock[] {
	if (contentBlocks && contentBlocks.length > 0) {
		return contentBlocksToBlocks(contentBlocks);
	}

	if (!body || typeof body !== "string" || !body.trim()) {
		return [
			{
				id: generateParseId(),
				type: "text",
				content: "",
				file: null,
				previewUrl: null
			}
		];
	}

	const parser = new DOMParser();
	const doc = parser.parseFromString(body, "text/html");

	const blocks = parseBody(doc);

	if (blocks.length === 0) {
		blocks.push({
			id: generateParseId(),
			type: "text",
			content: "",
			file: null,
			previewUrl: null
		});
	}

	return blocks;
}

export function announcementToFormValues(
	announcement: MeeAnnouncement
): AnnouncementFormValues {
	const [scheduledDate = "", scheduledTime = ""] = announcement.scheduledAt
		? announcement.scheduledAt.split(/T| /)
		: [];
	const [expiryDate = "", expiryTime = ""] = announcement.expiresAt
		? announcement.expiresAt.split(/T| /)
		: [];

	return {
		notificationType: announcement.body ? "announcement" : "push",
		title: announcement.title,
		body: announcement.body ?? "",
		heroImage: null,
		pushTitle: announcement.pushTitle ?? "",
		pushBody: announcement.pushBody ?? "",
		audienceType: announcement.audienceType,
		audiencePlatform: announcement.audiencePlatform ?? "both",
		audienceUserIds: announcement.audienceUserIds,
		audienceGroupIds: announcement.audienceGroupIds ?? [],
		hasSchedule: !!announcement.scheduledAt,
		scheduledDate,
		scheduledTime: scheduledTime.slice(0, 5),
		hasExpiry: !!announcement.expiresAt,
		expiryDate,
		expiryTime: expiryTime.slice(0, 5),
		blockFiles: [],
		contentBlocks: announcement.contentBlocks ?? []
	};
}

export function formatAudience(announcement: MeeAnnouncement): string {
	if (announcement.audienceType === "everyone") return "Everyone";
	if (announcement.audienceType === "platform") {
		if (announcement.audiencePlatform === "ios") return "iOS";
		if (announcement.audiencePlatform === "android") return "Android";
		return "Both platforms";
	}
	if (announcement.audienceType === "groups") {
		const count = announcement.audienceGroupIds?.length ?? 0;
		return `${count} group${count !== 1 ? "s" : ""}`;
	}
	const count = announcement.audienceUserIds.length;
	return `${count} user${count !== 1 ? "s" : ""}`;
}
